/**
 * openAlexService.ts - Integração com API OpenAlex
 * Bigwa v1.0 - Janeiro 2026
 * 
 * API Docs: https://docs.openalex.org/
 * Rate limit: 100k req/dia (sem autenticação)
 */

import {
  WorkCandidate,
  Author,
  mapOpenAlexType,
  normalizeWorkKey
} from '../../types/capturaInteligente';
import { logger } from '../../utils/logger';

const OPENALEX_BASE_URL = 'https://api.openalex.org';
const OPENALEX_EMAIL = 'contato@bigwa.app'; // Polite pool

interface OpenAlexAuthor {
  author?: {
    display_name?: string;
  };
  raw_author_name?: string;
}

interface OpenAlexWork {
  id: string;
  doi?: string;
  title?: string;
  authorships?: OpenAlexAuthor[];
  publication_year?: number;
  type?: string;
  primary_location?: {
    source?: {
      display_name?: string;
    };
  };
  host_venue?: {
    display_name?: string;
    publisher?: string;
  };
  biblio?: {
    volume?: string;
    issue?: string;
    first_page?: string;
    last_page?: string;
  };
  abstract_inverted_index?: Record<string, number[]>;
  cited_by_count?: number;
  related_works?: string[];
  referenced_works?: string[];
  concepts?: Array<{
    display_name: string;
    score: number;
  }>;
}

function parseOpenAlexAuthors(authorships: OpenAlexAuthor[] | undefined): Author[] {
  if (!authorships || authorships.length === 0) {
    return [{ firstName: '', lastName: 'Desconhecido' }];
  }
  
  return authorships.map(a => {
    const name = a.author?.display_name || a.raw_author_name || 'Desconhecido';
    const parts = name.split(' ');
    const lastName = parts.pop() || 'Desconhecido';
    const firstName = parts.join(' ');
    return { firstName, lastName };
  });
}

function reconstructAbstract(invertedIndex: Record<string, number[]> | undefined): string | undefined {
  if (!invertedIndex) return undefined;
  
  const words: [string, number][] = [];
  for (const [word, positions] of Object.entries(invertedIndex)) {
    for (const pos of positions) {
      words.push([word, pos]);
    }
  }
  words.sort((a, b) => a[1] - b[1]);
  return words.map(w => w[0]).join(' ').slice(0, 500);
}

function openAlexToCandidate(work: OpenAlexWork, relevanceScore = 50, relevanceReason = ''): WorkCandidate {
  const authors = parseOpenAlexAuthors(work.authorships);
  const year = work.publication_year || new Date().getFullYear();
  const title = work.title || 'Sem título';
  
  const pages = work.biblio?.first_page && work.biblio?.last_page
    ? `${work.biblio.first_page}-${work.biblio.last_page}`
    : work.biblio?.first_page;
  
  return {
    id: work.id || `openalex-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    title,
    authors,
    year,
    type: mapOpenAlexType(work.type || 'article'),
    doi: work.doi?.replace('https://doi.org/', ''),
    journal: work.primary_location?.source?.display_name || work.host_venue?.display_name,
    publisher: work.host_venue?.publisher,
    volume: work.biblio?.volume,
    issue: work.biblio?.issue,
    pages,
    abstract: reconstructAbstract(work.abstract_inverted_index),
    relevanceScore,
    relevanceReason,
    source: 'openalex',
    normalizedKey: normalizeWorkKey(
      authors.map(a => a.lastName),
      year,
      title
    ),
    citationCount: work.cited_by_count,
  };
}

/**
 * Busca por DOI específico no OpenAlex
 */
export async function searchByDOI(doi: string): Promise<WorkCandidate | null> {
  try {
    const cleanDoi = doi.replace(/^https?:\/\/(dx\.)?doi\.org\//, '');
    const response = await fetch(
      `${OPENALEX_BASE_URL}/works/doi:${encodeURIComponent(cleanDoi)}?mailto=${OPENALEX_EMAIL}`
    );

    if (!response.ok) {
      return null;
    }

    const work: OpenAlexWork = await response.json();
    if (!work || !work.title) return null;

    return openAlexToCandidate(work, 100, 'DOI exato (OpenAlex)');
  } catch (error) {
    logger.error('OpenAlexService', 'DOI search error:', error);
    return null;
  }
}

/**
 * Busca por título e autor
 */
export async function searchByTitleAuthor(
  title: string,
  author: string,
  year?: number | null
): Promise<WorkCandidate[]> {
  try {
    let filter = `title.search:${encodeURIComponent(title)}`;
    if (author) {
      filter += `,author.search:${encodeURIComponent(author)}`;
    }
    if (year) {
      filter += `,publication_year:${year}`;
    }
    
    const params = new URLSearchParams({
      'filter': filter,
      'per_page': '10',
      'sort': 'relevance_score:desc',
      'mailto': OPENALEX_EMAIL,
    });
    
    const response = await fetch(`${OPENALEX_BASE_URL}/works?${params}`);
    
    if (!response.ok) {
      logger.error('OpenAlexService', 'Error:', response.status);
      return [];
    }
    
    const data = await response.json();
    const works: OpenAlexWork[] = data.results || [];
    
    return works.map((work, index) => {
      const relevance = Math.max(40, 95 - (index * 8));
      return openAlexToCandidate(
        work,
        relevance,
        index === 0 ? 'Melhor correspondência' : `Resultado #${index + 1}`
      );
    });
  } catch (error) {
    logger.error('OpenAlexService', 'Search error:', error);
    return [];
  }
}

/**
 * Busca obras que citam uma obra específica (pelo DOI)
 */
export async function searchCitedBy(doi: string, limit = 10): Promise<WorkCandidate[]> {
  try {
    const params = new URLSearchParams({
      'filter': `cites:https://doi.org/${doi}`,
      'per_page': limit.toString(),
      'sort': 'cited_by_count:desc',
      'mailto': OPENALEX_EMAIL,
    });
    
    const response = await fetch(`${OPENALEX_BASE_URL}/works?${params}`);
    
    if (!response.ok) {
      return [];
    }
    
    const data = await response.json();
    const works: OpenAlexWork[] = data.results || [];
    
    return works.map(work => openAlexToCandidate(
      work,
      65,
      'Cita a obra buscada'
    ));
  } catch (error) {
    logger.error('OpenAlexService', 'CitedBy error:', error);
    return [];
  }
}

/**
 * Busca obras referenciadas por uma obra específica
 */
export async function searchReferences(openAlexId: string, limit = 10): Promise<WorkCandidate[]> {
  try {
    // Primeiro buscar a obra para pegar referenced_works
    const response = await fetch(`${OPENALEX_BASE_URL}/works/${openAlexId}?mailto=${OPENALEX_EMAIL}`);
    
    if (!response.ok) {
      return [];
    }
    
    const work: OpenAlexWork = await response.json();
    const referencedIds = work.referenced_works?.slice(0, limit) || [];
    
    if (referencedIds.length === 0) return [];
    
    // Buscar detalhes das referências
    const filter = referencedIds.map(id => id.replace('https://openalex.org/', '')).join('|');
    const refsResponse = await fetch(
      `${OPENALEX_BASE_URL}/works?filter=ids.openalex:${filter}&per_page=${limit}&mailto=${OPENALEX_EMAIL}`
    );
    
    if (!refsResponse.ok) {
      return [];
    }
    
    const refsData = await refsResponse.json();
    const refs: OpenAlexWork[] = refsData.results || [];
    
    return refs.map(ref => openAlexToCandidate(
      ref,
      60,
      'Referência citada'
    ));
  } catch (error) {
    logger.error('OpenAlexService', 'References error:', error);
    return [];
  }
}

/**
 * Busca obras relacionadas (related_works da API)
 */
export async function searchRelatedWorks(openAlexId: string, limit = 10): Promise<WorkCandidate[]> {
  try {
    const response = await fetch(`${OPENALEX_BASE_URL}/works/${openAlexId}?mailto=${OPENALEX_EMAIL}`);
    
    if (!response.ok) {
      return [];
    }
    
    const work: OpenAlexWork = await response.json();
    const relatedIds = work.related_works?.slice(0, limit) || [];
    
    if (relatedIds.length === 0) return [];
    
    const filter = relatedIds.map(id => id.replace('https://openalex.org/', '')).join('|');
    const relatedResponse = await fetch(
      `${OPENALEX_BASE_URL}/works?filter=ids.openalex:${filter}&per_page=${limit}&mailto=${OPENALEX_EMAIL}`
    );
    
    if (!relatedResponse.ok) {
      return [];
    }
    
    const relatedData = await relatedResponse.json();
    const related: OpenAlexWork[] = relatedData.results || [];
    
    return related.map(rel => openAlexToCandidate(
      rel,
      55,
      'Obra relacionada'
    ));
  } catch (error) {
    logger.error('OpenAlexService', 'Related error:', error);
    return [];
  }
}

/**
 * Busca outras obras do mesmo autor
 */
export async function searchByAuthor(
  authorName: string,
  excludeYear?: number,
  limit = 10
): Promise<WorkCandidate[]> {
  try {
    const params = new URLSearchParams({
      'filter': `author.search:${encodeURIComponent(authorName)}`,
      'per_page': limit.toString(),
      'sort': 'publication_year:desc',
      'mailto': OPENALEX_EMAIL,
    });
    
    const response = await fetch(`${OPENALEX_BASE_URL}/works?${params}`);
    
    if (!response.ok) {
      return [];
    }
    
    const data = await response.json();
    const works: OpenAlexWork[] = data.results || [];
    
    return works
      .filter(work => !excludeYear || work.publication_year !== excludeYear)
      .map(work => openAlexToCandidate(
        work,
        70,
        `Outra obra do autor`
      ));
  } catch (error) {
    logger.error('OpenAlexService', 'Author search error:', error);
    return [];
  }
}

/**
 * Busca por conceitos/temas
 */
export async function searchByConcept(
  concept: string,
  yearRange?: { start: number; end: number },
  limit = 10
): Promise<WorkCandidate[]> {
  try {
    let filter = `concepts.display_name.search:${encodeURIComponent(concept)}`;
    if (yearRange) {
      filter += `,publication_year:${yearRange.start}-${yearRange.end}`;
    }
    
    const params = new URLSearchParams({
      'filter': filter,
      'per_page': limit.toString(),
      'sort': 'cited_by_count:desc',
      'mailto': OPENALEX_EMAIL,
    });
    
    const response = await fetch(`${OPENALEX_BASE_URL}/works?${params}`);
    
    if (!response.ok) {
      return [];
    }
    
    const data = await response.json();
    const works: OpenAlexWork[] = data.results || [];
    
    return works.map(work => openAlexToCandidate(
      work,
      50,
      `Tema: ${concept}`
    ));
  } catch (error) {
    logger.error('OpenAlexService', 'Concept search error:', error);
    return [];
  }
}

export const openAlexService = {
  searchByDOI,
  searchByTitleAuthor,
  searchCitedBy,
  searchReferences,
  searchRelatedWorks,
  searchByAuthor,
  searchByConcept,
};
